import { ReplayEvent, ReplayFile, ReplayPlayer, ReplayState } from './types';
import { Team, Teams } from '../../shared/types/game';

type StateListener = (state: ReplayState) => void;

function buildInitialState(file: ReplayFile): ReplayState {
  const players: Record<string, ReplayPlayer> = {};
  for (const p of file.header.players) {
    players[p.profileId] = {
      profileId: p.profileId,
      username: p.username,
      avatarUrl: p.avatarUrl,
      avatarPublicId: p.avatarPublicId,
      location: p.location,
      parScore: 0,
      currentScore: 0,
      correctAnswer: false,
      incorrectAnswer: false,
      timerLimitReached: false,
      totalPenalty: 0,
      fuzzyScore: null,
      isPromptAnswer: false,
      confirmedReady: false,
      rank: null,
    };
  }
  return {
    gameClock: 0,
    gameStage: '',
    questionIndex: 0,
    revealAnswer: [],
    revealQuestion: null,
    players,
    gameOver: false,
    timerLimitReached: false,
    countdown: 0,
    nextQuestionDelayCount: 4,
    roundOver: false,
    gameStarted: false,
    questionMetadata: null,
    liveInputs: {},
    penalty: null,
    myTeam: file.header.teams ? buildInitialTeam(file.header.teams.teamA) : undefined,
    otherTeam: file.header.teams ? buildInitialTeam(file.header.teams.teamB) : undefined,
  };
}

function buildInitialTeam(t: { id: 'teamA' | 'teamB'; username: string; members: string[] }): Team {
  return {
    id: t.id,
    username: t.username,
    members: t.members,
    correctAnswer: false,
    correctAnswerPlayerUsername: '',
    currentScore: 0,
    fuzzyScore: 0,
    lastCorrectAnswerQuestionIndex: -1,
    parScore: 0,
    roundScores: {},
    timerLimitReached: false,
    totalPenalty: 0,
  };
}

function applyEvent(state: ReplayState, event: ReplayEvent): ReplayState {
  switch (event.type) {
    case 'game_property_update': {
      const { property, data } = event.payload as { property: keyof ReplayState; data: unknown };
      const next = { ...state, [property]: data };
      if (property === 'questionIndex') {
        // Mirror Room.resetStatesAndPlayers(): clear per-player feedback so old
        // "Try again" / "Time is up" messages don't bleed into the next question.
        const players = { ...next.players };
        for (const id of Object.keys(players)) {
          players[id] = {
            ...players[id],
            correctAnswer: false,
            incorrectAnswer: false,
            timerLimitReached: false,
            isPromptAnswer: false,
            fuzzyScore: null,
            currentScore: null,
          };
        }
        return {
          ...next,
          players,
          timerLimitReached: false,
          revealQuestion: null,
          liveInputs: {},
          penalty: null,
          myTeam: next.myTeam ? { ...next.myTeam, correctAnswer: false, correctAnswerPlayerUsername: '', timerLimitReached: false, fuzzyScore: 0, currentScore: 0 } : undefined,
          otherTeam: next.otherTeam ? { ...next.otherTeam, correctAnswer: false, correctAnswerPlayerUsername: '', timerLimitReached: false, fuzzyScore: 0, currentScore: 0 } : undefined,
        };
      }
      return next;
    }
    case 'player_update': {
      const player = event.payload.player as ReplayPlayer;
      return { ...state, players: { ...state.players, [player.profileId]: player } };
    }
    case 'player_input_live': {
      const { profileId, input } = event.payload as { profileId: string; input: string };
      return { ...state, liveInputs: { ...state.liveInputs, [profileId]: input } };
    }
    case 'penalty_event': {
      const { penalty } = event.payload as { penalty: ReplayState['penalty'] };
      return { ...state, penalty };
    }
    case 'team_update': {
      const { teams } = event.payload as { teams: Teams };
      return { ...state, myTeam: teams.teamA, otherTeam: teams.teamB };
    }
    default:
      return state;
  }
}

export class ReplayEngine {
  readonly duration: number;
  private state: ReplayState;
  private events: ReplayEvent[];
  private listeners: Set<StateListener> = new Set();
  private _currentTime: number = 0;
  private _isPlaying: boolean = false;
  private _speed: 1 | 2 | 4 = 1;
  private rafId: number | null = null;
  private lastFrameTime: number | null = null;
  private nextEventIndex: number = 0;

  constructor(private file: ReplayFile) {
    this.events = file.events;
    this.duration = this.events.length > 0 ? this.events[this.events.length - 1].t : 0;
    this.state = buildInitialState(file);
  }

  get currentTime(): number { return this._currentTime; }
  get isPlaying(): boolean { return this._isPlaying; }
  get speed(): 1 | 2 | 4 { return this._speed; }

  getState(): ReplayState { return this.state; }

  onStateChange(cb: StateListener): () => void {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  }

  play(): void {
    if (this._isPlaying) return;
    this._isPlaying = true;
    this.lastFrameTime = null;
    this.rafId = requestAnimationFrame(this.tick);
    this.notify();
  }

  pause(): void {
    this._isPlaying = false;
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    this.notify();
  }

  stop(): void {
    this.pause();
    this.seek(0);
  }

  seek(ms: number): void {
    const target = Math.max(0, Math.min(ms, this.duration));
    this._currentTime = target;
    this.state = buildInitialState(this.file);
    this.nextEventIndex = 0;
    for (let i = 0; i < this.events.length; i++) {
      if (this.events[i].t > target) break;
      this.state = applyEvent(this.state, this.events[i]);
      this.nextEventIndex = i + 1;
    }
    this.notify();
  }

  setSpeed(multiplier: 1 | 2 | 4): void {
    this._speed = multiplier;
  }

  private tick = (now: number): void => {
    if (!this._isPlaying) return;
    if (this.lastFrameTime !== null) {
      const elapsed = Math.min(now - this.lastFrameTime, 100);
      this._currentTime = Math.min(this._currentTime + elapsed * this._speed, this.duration);
      while (
        this.nextEventIndex < this.events.length &&
        this.events[this.nextEventIndex].t <= this._currentTime
      ) {
        this.state = applyEvent(this.state, this.events[this.nextEventIndex]);
        this.nextEventIndex++;
      }
      this.notify();
      if (this._currentTime >= this.duration) {
        this.pause();
        return;
      }
    }
    this.lastFrameTime = now;
    this.rafId = requestAnimationFrame(this.tick);
  };

  private notify(): void {
    this.listeners.forEach(cb => cb(this.state));
  }
}
